import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { TrackedMap } from 'tracked-built-ins';

/**
 * Event Data Service
 *
 * Central data store for event reference and timing data.
 * Manages WebSocket subscriptions and stores data in tracked Maps for reactivity.
 *
 * Delegates specialized concerns to:
 * - sequenceTracker: Message sequence gap detection
 * - dataMerger: REST data loading and merge locks
 * - messageProcessor: MQTT message handling
 *
 * All data uses UUID-based keys and fields:
 * - Reference data maps are keyed by `uuid`
 * - Timing records are keyed by `entryUuid`
 * - Relationships use UUID fields (e.g., classUuid, classGroupUuid)
 */
export default class EventDataService extends Service {
  @service websocket;
  @service sequenceTracker;
  @service dataMerger;
  @service messageProcessor;

  // Map of eventId -> event data structure
  @tracked events = new TrackedMap();

  // Version counter to force reactivity when data changes
  @tracked _dataVersion = 0;

  // Currently subscribed event IDs
  @tracked subscribedEvents = new Set();

  /**
   * Subscribe to an event's MQTT topics
   * Connects WebSocket and starts receiving data for the event
   * Returns a promise that resolves when WebSocket is connected
   */
  async subscribeToEvent(eventId) {
    if (this.subscribedEvents.has(eventId)) {
      console.log(`[EventData] Already subscribed to event ${eventId}`);
      return;
    }

    console.log(`[EventData] Subscribing to event ${eventId}`);

    // Initialize data structure for this event
    if (!this.events.has(eventId)) {
      this.events.set(eventId, this.createEventDataStructure(eventId));
    }

    // Connect WebSocket for this event and wait for connection
    await this.websocket.connect(eventId);
    this.subscribedEvents.add(eventId);

    // Set up message handler
    this.setupMessageHandler();
  }

  /**
   * Unsubscribe from an event's MQTT topics
   */
  unsubscribeFromEvent(eventId) {
    console.log(`[EventData] Unsubscribing from event ${eventId}`);

    this.subscribedEvents.delete(eventId);

    // If no more subscriptions, disconnect WebSocket
    if (this.subscribedEvents.size === 0) {
      this.websocket.disconnect();
    }
  }

  /**
   * Create initial data structure for an event
   * All maps use UUID-based keys for consistency
   */
  createEventDataStructure(eventId) {
    return {
      eventId,

      // Reference Data (keyed by uuid)
      event: null,
      classGroups: new TrackedMap(), // uuid -> classGroup
      classes: new TrackedMap(),      // uuid -> class
      entries: new TrackedMap(),      // uuid -> entry
      courses: new TrackedMap(),      // uuid -> course
      tieTimingRecords: new TrackedMap(), // entryUuid -> tieRecord
      penaltyTimingRecords: new TrackedMap(), // entryUuid -> array of penalties

      // Timing Data (keyed by entryUuid)
      startRecords: new TrackedMap(),      // entryUuid -> startRecord
      finishRecords: new TrackedMap(),     // entryUuid -> finishRecord
      intermediateRecords: new TrackedMap(), // entryUuid -> array of intermediate records

      // Metadata
      lastUpdated: null,
      snapshotComplete: false,
      snapshotProgress: {
        event: false,
        classgroups: false,
        classes: false,
        entries: false,
        courses: false,
        tietimingrecords: false,
        penaltytimingrecords: false,
        timing: false
      }
    };
  }

  /**
   * Set up message handler to route MQTT messages
   */
  setupMessageHandler() {
    // We'll process messages from websocket.messages
    // Since messages array is tracked, we can observe changes
    // For now, we'll poll in the component/route
    // TODO: Consider using a more reactive approach
  }

  // ============================================
  // Delegation to sequenceTracker service
  // ============================================

  /**
   * Set callback for sequence gap detection
   * Called when a gap is detected, allowing the route to trigger a refresh
   */
  setSequenceGapCallback(callback) {
    this.sequenceTracker.setSequenceGapCallback(callback);
  }

  /**
   * Reset sequence tracking for an event (call after REST load)
   */
  resetSequenceTracking(eventId) {
    this.sequenceTracker.resetSequenceTracking(eventId);
  }

  // ============================================
  // Delegation to messageProcessor service
  // ============================================

  /**
   * Process a single MQTT message
   * Delegates to messageProcessor service
   */
  processMessage(message) {
    this.messageProcessor.processMessage(message);
  }

  // ============================================
  // Delegation to dataMerger service
  // ============================================

  /**
   * Load initial reference data from REST API response
   * Delegates to dataMerger service
   */
  loadReferenceData(eventId, referenceData) {
    this.dataMerger.loadReferenceData(eventId, referenceData);
  }

  /**
   * Load initial timing data from REST API response
   * Delegates to dataMerger service
   * Returns any queued messages that need processing
   */
  loadTimingData(eventId, timingData) {
    const pendingMessages = this.dataMerger.loadTimingData(eventId, timingData);

    // Process any messages that were queued during the merge
    if (pendingMessages && pendingMessages.length > 0) {
      console.log(`[EventData] Processing ${pendingMessages.length} queued messages after merge`);
      for (const message of pendingMessages) {
        this.messageProcessor._processMessageInternal(message);
      }
    }

    // Check if all data is now complete
    this.messageProcessor.checkSnapshotCompletion(eventId);
  }

  // ============================================
  // Data accessors (kept in this service)
  // ============================================

  /**
   * Get all data for an event
   */
  getEventData(eventId) {
    return this.events.get(eventId);
  }

  /**
   * Get classgroups for an event
   */
  getClassGroups(eventId) {
    const eventData = this.events.get(eventId);
    if (!eventData) return [];

    return Array.from(eventData.classGroups.values());
  }

  /**
   * Get entries for a specific classgroup
   * @param {string} eventId - The event ID
   * @param {string} classGroupUuid - The classgroup UUID
   */
  getEntriesForClassGroup(eventId, classGroupUuid) {
    const eventData = this.events.get(eventId);
    if (!eventData) {
      console.warn(`[EventData] getEntriesForClassGroup: no event data for ${eventId}`);
      return [];
    }

    // Get all classes for this classgroup (using classGroupUuid)
    const allClasses = Array.from(eventData.classes.values());
    const classUuidsInGroup = allClasses
      .filter(cls => cls.classGroupUuid === classGroupUuid)
      .map(cls => cls.uuid);

    console.log(`[EventData] getEntriesForClassGroup(${eventId}, ${classGroupUuid}):`, {
      totalClasses: allClasses.length,
      classesInGroup: classUuidsInGroup.length,
      totalEntries: eventData.entries.size,
      sampleClass: allClasses[0],
      sampleEntry: Array.from(eventData.entries.values())[0]
    });

    // Get all entries for these classes (using classUuid)
    const entries = Array.from(eventData.entries.values())
      .filter(entry => classUuidsInGroup.includes(entry.classUuid));

    console.log(`[EventData] Found ${entries.length} entries for classgroup ${classGroupUuid}`);

    return entries;
  }

  /**
   * Get course for a classgroup
   * @param {string} eventId - The event ID
   * @param {string} classGroupUuid - The classgroup UUID
   */
  getCourseForClassGroup(eventId, classGroupUuid) {
    const eventData = this.events.get(eventId);
    if (!eventData) return null;

    const classGroup = eventData.classGroups.get(classGroupUuid);
    if (!classGroup || !classGroup.courseUuid) return null;

    return eventData.courses.get(classGroup.courseUuid);
  }

  willDestroy() {
    super.willDestroy(...arguments);

    // Unsubscribe from all events
    this.subscribedEvents.forEach(eventId => {
      this.unsubscribeFromEvent(eventId);
    });
  }
}
